import React, { useEffect, useRef, useState } from 'react';
import Video, { Room, Participant, RemoteTrack, RemoteVideoTrack, LocalVideoTrack, LocalAudioTrack, TwilioError } from 'twilio-video';
import { LocalContainer, RemoteContainer, RemoteMicOffContainer, UserPlaceholder, VideoContainer } from './style';
import CallActions from '../call-actions';
import { UserIcon } from '../../../components/icons/user-icon';
import useUserTalk from '../../../hooks/use-user-talk';
import { useAppDispatch } from '../../../../common/store';
import { endCall } from '../../../../common/store/calls/calls-slice';
import { MicOffIcon } from '../../../components/icons/chat-actions/mic-off-icon';
interface VideoCallProps {
  isMicOn: boolean;
  isCameraOn: boolean;
  room: Room;
  agentName?: string;
  agentPhotoURL?: string;
}

const VideoCall: React.FC<VideoCallProps> = ({ room, isMicOn, isCameraOn,agentName,agentPhotoURL }) => {
  const [isParticipantVideoOn, setIsParticipantVideoOn] = useState(false);
  const [isParticipantMicOn, setIsParticipantMicOn] = useState(false);
  const [isParticipantConnected, setIsParticipantConnected] = useState(false);
  const dispatch = useAppDispatch()
  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const isTalking = useUserTalk(isMicOn);

  const muteVideo = () => {
    if (!room) return;
    room.localParticipant.videoTracks.forEach((pub) => {
      pub.track.stop();
      pub.unpublish();
      pub.track.disable();
    })
    if (localVideoRef.current) {
      const videoElements = localVideoRef.current.getElementsByTagName('video');
      while (videoElements.length > 0) {
        videoElements[0].remove();
      }
    }
  }

  const unmuteVideo = () => {
    if (!room) return;
    Video.createLocalVideoTrack().then((track: LocalVideoTrack) => {
      if (localVideoRef.current) {
        localVideoRef.current.appendChild(track.attach());
      }
      room.localParticipant.publishTrack(track);
    });
  }

  const unMuteMic = () => {
    if (!room) return;
    Video.createLocalAudioTrack().then((track: LocalAudioTrack) => {
      if (localVideoRef.current) {
        localVideoRef.current.appendChild(track.attach());
      }
      room.localParticipant.publishTrack(track);
    });
  }

  const muteMic = () => {
    if (!room) return;
    room.localParticipant.audioTracks.forEach((pub) => {
      pub.track.stop();
      pub.unpublish();
      pub.track.disable();
    })

    if (localVideoRef.current) {
      const audioElements = localVideoRef.current.getElementsByTagName('audio');
      while (audioElements.length > 0) {
        audioElements[0].remove();
      }
    }
  }

  useEffect(() => {
    if (isMicOn) {
      unMuteMic();
    } else {
      muteMic();
    }
  }, [isMicOn])

  useEffect(() => {
    if (isCameraOn) {
      unmuteVideo();
    } else {
      muteVideo();
    }
  }, [isCameraOn])

  useEffect(() => {
    if (!room) return;

    // Handle participants
    room.participants.forEach(participantConnected);
    room.on('participantConnected', participantConnected);
    room.on('participantDisconnected', participantDisconnected);
    const onDisconnected = (_: Room, error: TwilioError) => {
      if (error) {
        console.error('Disconnected due to error:', error);
      }
      muteVideo();
      muteMic();
      room.participants.forEach(participantDisconnected);
    };

    room.on('disconnected', onDisconnected);

    return () => {
      room.disconnect()
      room.off('participantConnected', participantConnected);
      room.off('participantDisconnected', participantDisconnected);
      room.off('disconnected', onDisconnected);
    }
  }, [room])

  const participantConnected = (participant: Participant) => {
    setIsParticipantConnected(true);

    const div = document.createElement('div');
    div.id = participant.sid;
    div.style.height = '100%';
    div.style.display = 'flex';
    div.style.alignItems = 'center';

    participant.on('trackSubscribed', (track: RemoteTrack) => trackSubscribed(div, track));
    participant.on('trackUnsubscribed', trackUnsubscribed);

    participant.tracks.forEach((publication: any) => {
      if (publication.isSubscribed && publication.track) {
        trackSubscribed(div, publication.track);
      }
    });

    if (remoteVideoRef.current) {
      remoteVideoRef.current.appendChild(div);
    }
  };

  const participantDisconnected = (participant: Participant) => {
    setIsParticipantConnected(false);

    dispatch(endCall());
    const participantDiv = document.getElementById(participant.sid);
    if (participantDiv) {
      participantDiv.remove();
    }
  };

  const trackSubscribed = (div: HTMLElement, track: RemoteTrack) => {
    if (track.kind === 'video') {
      div.appendChild(track.attach());
      setIsParticipantVideoOn(true);
    }
    if (track.kind === 'audio') {
      div.appendChild(track.attach());
      setIsParticipantMicOn(true);
    }
  };

  const trackUnsubscribed = (track: RemoteTrack) => {
    if (track.kind === 'audio') {
      setIsParticipantMicOn(false);
    }
    if (track.kind === 'video') {
      setIsParticipantVideoOn(false);
    }
    if (track.kind === 'video' || track.kind === 'audio') {
      track.detach().forEach((element) => element.remove());
    }
  };

  return (
    <VideoContainer className='voa_video_container'>
      <RemoteContainer className='voa_remote_video_container' ref={remoteVideoRef}>
        {!isParticipantConnected ? (
            <span>Waiting For {agentName} to Join...</span>
        ) : (
          <>
            {!isParticipantVideoOn && (
                <UserPlaceholder className='user-placeholder' isTalking={false}>
                {
                  agentPhotoURL ? 
                  <img
                  src={agentPhotoURL}
                  alt="avatar"
                  width={50}
                  height={50}
                  style={{ borderRadius: '50%' }}
                />
                : <UserIcon />
                }
                </UserPlaceholder>
            )}
          
            {!isParticipantMicOn && (
              <RemoteMicOffContainer>
                <MicOffIcon />
              </RemoteMicOffContainer>
            )}
          </>
        )}
      </RemoteContainer>
      <LocalContainer isCameraOn={isCameraOn} className='voa_local_video_container' ref={localVideoRef}>
        {!isCameraOn && (
          <UserPlaceholder isTalking={isMicOn && isTalking}>
            <UserIcon />
          </UserPlaceholder>
        )}
      </LocalContainer>
      <CallActions />
    </VideoContainer>
  );
};

export default VideoCall;
